if (!customElements.get("el-product-form")) {
  customElements.define(
    "el-product-form",
    class ElProductForm extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === "true") return;
        this.dataset.ready = "true";

        this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
        this.productData = this.readProductData();

        this.variantInput = this.querySelector("[data-el-variant-id]");
        this.bundleOptions = Array.from(
          this.querySelectorAll("[data-bundle-option]"),
        );
        this.atcButton = this.querySelector("[data-el-atc-button]");
        this.atcText = this.querySelector("[data-el-atc-text]");
        this.atcPrice = this.querySelector("[data-el-atc-price]");
        this.atcBundleLine = this.querySelector("[data-el-atc-bundle-line]");
        this.atcBundleTitle = this.querySelector("[data-el-atc-bundle-title]");
        this.atcInlineMeta = this.querySelector("[data-el-atc-inline-meta]");
        this.showBundleTitleInButton =
          this.atcButton?.dataset.showBundleTitle === "true";

        this.onBundleClick = this.onBundleClick.bind(this);
        this.onAddToCart = this.onAddToCart.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onThemeVariantChange = this.onThemeVariantChange.bind(this);

        this.bundleOptions.forEach((option) => {
          option.addEventListener("click", this.onBundleClick);
        });

        if (this.atcButton) {
          this.atcButton.addEventListener("click", this.onAddToCart);
        }

        document.addEventListener("change", this.onVariantChange);
        document.addEventListener("variant:change", this.onThemeVariantChange);
        document.addEventListener(
          "product:variant-change",
          this.onThemeVariantChange,
        );

        this.ensureDefaultBundle();

        const initialVariantId =
          this.variantInput?.value || this.findExternalVariantInput()?.value;

        this.setVariant(initialVariantId);
        this.updatePrices();
      }

      disconnectedCallback() {
        this.bundleOptions.forEach((option) => {
          option.removeEventListener("click", this.onBundleClick);
        });

        if (this.atcButton) {
          this.atcButton.removeEventListener("click", this.onAddToCart);
        }

        document.removeEventListener("change", this.onVariantChange);
        document.removeEventListener(
          "variant:change",
          this.onThemeVariantChange,
        );
        document.removeEventListener(
          "product:variant-change",
          this.onThemeVariantChange,
        );
      }

      readProductData() {
        const jsonEl = this.querySelector("[data-el-product-json]");

        if (!jsonEl) return null;

        try {
          return JSON.parse(jsonEl.textContent);
        } catch (error) {
          console.warn("Product JSON could not be read:", error);
          return null;
        }
      }

      findExternalVariantInput() {
        const scope = this.closest(".shopify-section") || document;

        return scope.querySelector(
          'form[action*="/cart/add"] select[name="id"], form[action*="/cart/add"] input[name="id"]',
        );
      }

      getVariantById(id) {
        if (!this.productData || !Array.isArray(this.productData.variants))
          return null;

        return this.productData.variants.find((variant) => {
          return String(variant.id) === String(id);
        });
      }

      setVariant(id) {
        if (!id) return;

        const variant = this.getVariantById(id);
        if (!variant) return;

        this.currentVariant = variant;

        if (this.variantInput) {
          this.variantInput.value = variant.id;
        }

        this.updatePrices();
      }

      onVariantChange(event) {
        const target = event.target;

        if (!target.matches('select[name="id"], input[name="id"]')) return;
        if (target === this.variantInput) return;

        this.setVariant(target.value);
      }

      onThemeVariantChange(event) {
        const detail = event.detail || {};
        const variant =
          detail.variant ||
          detail.productVariant ||
          detail.currentVariant ||
          null;

        if (variant && variant.id) {
          this.setVariant(variant.id);
        }
      }

      ensureDefaultBundle() {
        if (!this.bundleOptions.length) return;

        let selected =
          this.bundleOptions.find(
            (option) => option.dataset.defaultSelected === "true",
          ) ||
          this.bundleOptions.find((option) =>
            option.classList.contains("is-active"),
          ) ||
          this.bundleOptions[0];

        this.selectBundle(selected);
      }

      onBundleClick(event) {
        this.selectBundle(event.currentTarget);
      }

      selectBundle(selectedOption) {
        if (!selectedOption) return;

        this.bundleOptions.forEach((option) => {
          const isActive = option === selectedOption;

          option.classList.toggle("is-active", isActive);
          option.setAttribute("aria-pressed", isActive ? "true" : "false");
        });

        this.updatePrices();
      }

      getSelectedBundle() {
        return (
          this.bundleOptions.find((option) =>
            option.classList.contains("is-active"),
          ) || null
        );
      }

      getBundlePrice(option) {
        if (!this.currentVariant || !option) return 0;

        const price = Number(this.currentVariant.price || 0);
        const qty = Number(option.dataset.quantity || 1);
        const discountPercent = Number(option.dataset.discountPercent || 0);
        const multiplier = (100 - discountPercent) / 100;

        return Math.round(price * qty * multiplier);
      }

      getBundleComparePrice(option) {
        if (!this.currentVariant || !option) return 0;

        const price = Number(this.currentVariant.price || 0);
        const compareBase = Number(
          this.currentVariant.compare_at_price || price,
        );
        const qty = Number(option.dataset.quantity || 1);
        const compareQty = Number(option.dataset.compareQuantity || 0);

        if (compareQty > 0) {
          return Math.round(compareBase * compareQty);
        }

        return Math.round(compareBase * qty);
      }

      getCurrentAtcPrice() {
        if (!this.currentVariant) return 0;

        const selectedBundle = this.getSelectedBundle();

        if (selectedBundle) {
          return this.getBundlePrice(selectedBundle);
        }

        return Number(this.currentVariant.price || 0);
      }

      updatePrices() {
        if (!this.currentVariant) return;

        this.bundleOptions.forEach((option) => {
          this.updateBundleOptionPrice(option);
        });

        if (this.atcPrice) {
          this.atcPrice.textContent = this.formatMoney(
            this.getCurrentAtcPrice(),
          );
        }

        this.updateAtcButtonContent();
        this.dispatchStickyCtaUpdate();
      }

      updateBundleOptionPrice(option) {
        if (!option) return;

        const price = this.getBundlePrice(option);
        const comparePrice = this.getBundleComparePrice(option);
        const displayMode = option.dataset.priceDisplayMode || "total";
        const perProductQuantity = Math.max(
          1,
          Number(option.dataset.perProductQuantity || option.dataset.quantity || 1),
        );
        const perProductLabel = option.dataset.perProductLabel || "";

        const priceEl = option.querySelector("[data-bundle-price]");
        const compareEl = option.querySelector("[data-bundle-compare]");

        if (priceEl) {
          if (displayMode === "per_product") {
            const perProductPrice = Math.round(price / perProductQuantity);
            const labelHtml = perProductLabel
              ? ` <span class="el-bundle-option__price-unit">${this.escapeHtml(
                  perProductLabel,
                )}</span>`
              : "";

            priceEl.innerHTML = `${this.formatMoney(perProductPrice)}${labelHtml}`;
          } else {
            priceEl.textContent = this.formatMoney(price);
          }
        }

        if (compareEl) {
          compareEl.textContent = this.formatMoney(comparePrice);
          compareEl.classList.toggle(
            "is-hidden",
            displayMode === "per_product" || comparePrice <= price,
          );
        }
      }

      updateAtcButtonContent() {
        if (!this.atcButton || !this.atcText) return;

        if (this.currentVariant.available) {
          this.atcButton.disabled = false;
          this.atcText.textContent = this.atcButton.dataset.text || "ADD TO CART";

          if (this.showBundleTitleInButton && this.atcBundleLine) {
            const selectedBundle = this.getSelectedBundle();
            const bundleTitle = selectedBundle?.dataset.bundleTitle || "";

            this.atcBundleLine.hidden = false;

            if (this.atcBundleTitle) {
              this.atcBundleTitle.textContent = bundleTitle;
            }
          }
        } else {
          this.atcButton.disabled = true;
          this.atcText.textContent = "SOLD OUT";

          if (this.atcBundleLine) {
            this.atcBundleLine.hidden = true;
          }
        }
      }

      dispatchStickyCtaUpdate() {
        if (!this.currentVariant) return;

        const selectedBundle = this.getSelectedBundle();
        const price = this.getCurrentAtcPrice();

        let comparePrice = 0;

        if (selectedBundle) {
          comparePrice = this.getBundleComparePrice(selectedBundle);
        } else {
          comparePrice = Number(this.currentVariant.compare_at_price || 0);
        }

        const showComparePrice = comparePrice > price;

        this.dispatchEvent(
          new CustomEvent("el-product-form:update", {
            bubbles: true,
            detail: {
              price,
              priceHtml: this.formatMoney(price),
              comparePrice,
              comparePriceHtml: showComparePrice
                ? this.formatMoney(comparePrice)
                : "",
              showComparePrice,
              available: Boolean(this.currentVariant.available),
              variantId: this.currentVariant.id,
              bundleTitle: selectedBundle?.dataset.bundleTitle || "",
              quantity: selectedBundle
                ? Number(selectedBundle.dataset.quantity || 1)
                : 1,
            },
          }),
        );
      }

      escapeHtml(value) {
        return String(value || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
      }

      formatMoney(cents) {
        cents = Number(cents || 0);

        if (
          window.Shopify &&
          typeof window.Shopify.formatMoney === "function"
        ) {
          return window.Shopify.formatMoney(cents, this.moneyFormat);
        }

        return `$${(cents / 100).toFixed(2)}`;
      }

      async onAddToCart() {
        if (
          !this.atcButton ||
          !this.currentVariant ||
          !this.currentVariant.available
        )
          return;

        const selectedBundle = this.getSelectedBundle();

        const quantity = selectedBundle
          ? Number(selectedBundle.dataset.quantity || 1)
          : 1;

        const items = [
          {
            id: Number(this.currentVariant.id),
            quantity,
          },
        ];

        if (selectedBundle) {
          const giftVariantIds = (selectedBundle.dataset.giftVariantIds || "")
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);

          giftVariantIds.forEach((giftId) => {
            items.push({
              id: Number(giftId),
              quantity: 1,
              properties: {
                "_Free gift": "Yes",
                _Bundle: selectedBundle.dataset.bundleTitle || "Bundle",
              },
            });
          });
        }

        const originalText = this.atcText ? this.atcText.textContent : "";
        const root = window.Shopify?.routes?.root || "/";

        try {
          this.atcButton.classList.add("is-loading");
          this.atcButton.disabled = true;

          if (this.atcText) {
            this.atcText.textContent = "ADDING";
          }

          const cartDrawer = document.querySelector("cart-drawer");
          const sections = this.getShrineCartSections(cartDrawer);

          const response = await fetch(`${root}cart/add.js`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
              items,
              sections,
              sections_url: window.location.pathname,
            }),
          });

          const parsedState = await response.json();

          if (!response.ok) {
            throw new Error(
              parsedState.description ||
                parsedState.message ||
                "Could not add item to cart.",
            );
          }

          /*
      Important:
      Shrine/Dawn cart drawer needs parsedState.id.
      When using items: [], Shopify returns parsedState.items instead.
    */
          if (
            !parsedState.id &&
            parsedState.items &&
            parsedState.items.length
          ) {
            parsedState.id = parsedState.items[0].id;
          }

          /*
      If Shopify did not return sections for any reason,
      fetch the fresh drawer HTML manually.
    */
          if (!parsedState.sections) {
            parsedState.sections = await this.fetchFreshCartSections(sections);
          }

          this.renderShrineCartDrawer(parsedState, cartDrawer);
        } catch (error) {
          console.error(error);
          alert(error.message || "Something went wrong. Please try again.");
        } finally {
          this.atcButton.classList.remove("is-loading");
          this.atcButton.disabled = false;

          if (this.atcText) {
            this.atcText.textContent = originalText;
          }

          this.updatePrices();
        }
      }

      getShrineCartSections(cartDrawer) {
        if (
          cartDrawer &&
          typeof cartDrawer.getSectionsToRender === "function"
        ) {
          return cartDrawer.getSectionsToRender().map((section) => section.id);
        }

        return ["cart-drawer", "cart-icon-bubble"];
      }

      async fetchFreshCartSections(sections) {
        const root = window.Shopify?.routes?.root || "/";
        const sectionUrl = `${root}?sections=${sections.join(",")}`;

        const response = await fetch(sectionUrl, {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        return await response.json();
      }

      renderShrineCartDrawer(parsedState, cartDrawer) {
        cartDrawer = cartDrawer || document.querySelector("cart-drawer");

        /*
    Best option for Shrine:
    use the theme's native drawer renderer.
  */
        if (
          cartDrawer &&
          typeof cartDrawer.renderContents === "function" &&
          parsedState.sections
        ) {
          cartDrawer.renderContents(parsedState);

          setTimeout(() => {
            this.cleanShrineEmptyCartState();
          }, 50);

          return;
        }

        /*
    Fallback:
    manually replace the drawer HTML.
  */
        this.manuallyRenderCartSections(parsedState.sections);
        this.cleanShrineEmptyCartState();
        this.forceOpenShrineCartDrawer();
      }

      manuallyRenderCartSections(sections) {
        if (!sections) return;

        Object.keys(sections).forEach((sectionId) => {
          const html = sections[sectionId];
          if (!html) return;

          const parsedHTML = new DOMParser().parseFromString(html, "text/html");

          if (sectionId === "cart-drawer") {
            const newCartDrawer = parsedHTML.querySelector("#CartDrawer");
            const currentCartDrawer = document.querySelector("#CartDrawer");

            if (newCartDrawer && currentCartDrawer) {
              currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
            }

            return;
          }

          if (sectionId === "cart-icon-bubble") {
            const newBubble =
              parsedHTML.querySelector("#cart-icon-bubble") ||
              parsedHTML.querySelector("[id*='cart-icon-bubble']");

            const currentBubble =
              document.querySelector("#cart-icon-bubble") ||
              document.querySelector("[id*='cart-icon-bubble']");

            if (newBubble && currentBubble) {
              currentBubble.innerHTML = newBubble.innerHTML;
            }
          }
        });
      }

      cleanShrineEmptyCartState() {
        const cartDrawer = document.querySelector("cart-drawer");
        const cartDrawerItems = document.querySelector("cart-drawer-items");
        const drawerInner = document.querySelector(".drawer__inner");
        const checkoutButton = document.querySelector("#CartDrawer-Checkout");

        if (cartDrawer) {
          cartDrawer.classList.remove("is-empty");
        }

        if (cartDrawerItems) {
          cartDrawerItems.classList.remove("is-empty");
        }

        if (drawerInner) {
          drawerInner.classList.remove("is-empty");
        }

        if (checkoutButton) {
          checkoutButton.disabled = false;
          checkoutButton.removeAttribute("disabled");
        }
      }

      forceOpenShrineCartDrawer() {
        const cartDrawer = document.querySelector("cart-drawer");

        if (!cartDrawer) return;

        if (typeof cartDrawer.open === "function") {
          cartDrawer.open();
          return;
        }

        cartDrawer.classList.add("animate", "active");
        cartDrawer.setAttribute("open", "");
        document.body.classList.add("overflow-hidden");
      }
    },
  );
}
